import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeSanitize from "rehype-sanitize";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  oneDark,
  oneLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { Copy, Check } from "lucide-react";

const MarkdownRenderer = ({
  content,
  className = "",
  compact = false,
  theme = "light",
  enhanced = false,
}) => {
  const [copiedCode, setCopiedCode] = React.useState(null);

  if (!content) return null;

  const copyToClipboard = async (code, index) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(index);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Failed to copy code: ", err);
    }
  };

  const codeStyle = theme === "dark" ? oneDark : oneLight;

  return (
    <div
      className={`markdown-content ${
        enhanced ? "enhanced-markdown" : ""
      } ${className}`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          // Customize how different markdown elements are rendered
          h1: ({ node, ...props }) => (
            <h1
              className={
                enhanced
                  ? "text-3xl font-bold mb-6 mt-8 text-gray-900 border-b border-gray-200 pb-3"
                  : `font-bold text-gray-900 ${
                      compact ? "text-xl mb-3 mt-4" : "text-2xl mb-4"
                    }`
              }
              {...props}
            />
          ),
          h2: ({ node, ...props }) => (
            <h2
              className={
                enhanced
                  ? "text-2xl font-bold mb-4 mt-6 text-gray-900 border-b border-gray-100 pb-2"
                  : `font-bold text-gray-900 ${
                      compact ? "text-lg mb-2 mt-3" : "text-xl mb-3"
                    }`
              }
              {...props}
            />
          ),
          h3: ({ node, ...props }) => (
            <h3
              className={
                enhanced
                  ? "text-xl font-bold mb-3 mt-5 text-gray-900"
                  : `font-bold text-gray-900 ${
                      compact ? "text-base mb-2 mt-3" : "text-lg mb-2"
                    }`
              }
              {...props}
            />
          ),
          h4: ({ node, ...props }) => (
            <h4
              className={
                enhanced
                  ? "text-lg font-semibold mb-2 mt-4 text-gray-900"
                  : `font-bold text-gray-900 ${
                      compact ? "text-sm mb-1 mt-2" : "text-base mb-2"
                    }`
              }
              {...props}
            />
          ),
          h5: ({ node, ...props }) => (
            <h5
              className={
                enhanced
                  ? "text-base font-semibold mb-2 mt-3 text-gray-900"
                  : `font-bold text-gray-900 ${
                      compact ? "text-sm mb-1 mt-2" : "text-sm mb-2"
                    }`
              }
              {...props}
            />
          ),
          h6: ({ node, ...props }) => (
            <h6
              className={
                enhanced
                  ? "text-sm font-semibold mb-2 mt-3 text-gray-900"
                  : `font-bold text-gray-900 ${
                      compact ? "text-xs mb-1 mt-2" : "text-xs mb-2"
                    }`
              }
              {...props}
            />
          ),
          p: ({ node, ...props }) => (
            <p
              className={
                enhanced
                  ? "mb-4 text-gray-800 leading-7 text-base"
                  : `text-gray-700 leading-relaxed ${compact ? "mb-2" : "mb-4"}`
              }
              {...props}
            />
          ),
          ul: ({ node, ...props }) => (
            <ul
              className={
                enhanced
                  ? "list-disc list-outside ml-6 mb-4 space-y-1 text-gray-800"
                  : `list-disc list-inside text-gray-700 ${
                      compact ? "mb-2" : "mb-4"
                    }`
              }
              {...props}
            />
          ),
          ol: ({ node, ...props }) => (
            <ol
              className={
                enhanced
                  ? "list-decimal list-outside ml-6 mb-4 space-y-1 text-gray-800"
                  : `list-decimal list-inside text-gray-700 ${
                      compact ? "mb-2" : "mb-4"
                    }`
              }
              {...props}
            />
          ),
          li: ({ node, ...props }) => (
            <li
              className={enhanced ? "leading-6 text-gray-800" : "mb-1"}
              {...props}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              className={
                enhanced
                  ? "border-l-4 border-blue-400 bg-blue-50 pl-6 pr-4 py-3 my-4 italic text-gray-700 rounded-r-lg"
                  : `border-l-4 border-blue-500 pl-4 italic text-gray-600 ${
                      compact ? "mb-2" : "mb-4"
                    }`
              }
              {...props}
            />
          ),
          code: ({ node, inline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || "");
            const language = match ? match[1] : "";
            const codeString = String(children).replace(/\n$/, "");
            const codeIndex = `${language}-${codeString.slice(0, 20)}`;

            if (!inline && language) {
              return (
                <div className={`relative group ${compact ? "mb-2" : "mb-4"}`}>
                  <div className="flex items-center justify-between bg-gray-800 text-gray-200 px-3 py-2 rounded-t-lg text-sm">
                    <span className="font-mono text-xs uppercase tracking-wide">
                      {language}
                    </span>
                    <button
                      onClick={() => copyToClipboard(codeString, codeIndex)}
                      className="flex items-center space-x-1 px-2 py-1 rounded text-xs bg-gray-700 hover:bg-gray-600 transition-colors"
                      title="Copy code"
                    >
                      {copiedCode === codeIndex ? (
                        <>
                          <Check className="w-3 h-3" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                  <SyntaxHighlighter
                    style={codeStyle}
                    language={language}
                    customStyle={{
                      margin: 0,
                      borderRadius: "0 0 0.5rem 0.5rem",
                      fontSize: compact ? "0.8rem" : "0.875rem",
                      lineHeight: "1.5",
                    }}
                    showLineNumbers={
                      enhanced
                        ? codeString.split("\\n").length > 10
                        : codeString.split("\\n").length > 5
                    }
                    {...props}
                  >
                    {codeString}
                  </SyntaxHighlighter>
                </div>
              );
            }

            return (
              <code
                className={
                  enhanced
                    ? "bg-gray-100 text-pink-600 px-1.5 py-0.5 rounded text-sm font-mono border"
                    : "bg-gray-100 text-red-600 px-1 py-0.5 rounded text-sm font-mono"
                }
                {...props}
              >
                {children}
              </code>
            );
          },
          pre: ({ node, children, ...props }) => (
            <pre
              className={`bg-gray-100 text-gray-800 p-3 rounded text-sm font-mono overflow-x-auto ${
                compact ? "mb-2" : "mb-4"
              }`}
              {...props}
            >
              {children}
            </pre>
          ),
          strong: ({ node, ...props }) => (
            <strong className="font-bold text-gray-900" {...props} />
          ),
          em: ({ node, ...props }) => (
            <em className="italic text-gray-700" {...props} />
          ),
          a: ({ node, ...props }) => (
            <a
              className="text-blue-600 hover:text-blue-800 underline"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            />
          ),
          table: ({ node, ...props }) => (
            <div className={`overflow-x-auto ${compact ? "mb-2" : "mb-4"}`}>
              <table className="min-w-full border border-gray-300" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead className="bg-gray-50" {...props} />
          ),
          tbody: ({ node, ...props }) => (
            <tbody className="bg-white" {...props} />
          ),
          tr: ({ node, ...props }) => (
            <tr className="border-b border-gray-200" {...props} />
          ),
          th: ({ node, ...props }) => (
            <th
              className="px-4 py-2 text-left font-medium text-gray-900 border-r border-gray-200"
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td
              className="px-4 py-2 text-gray-700 border-r border-gray-200"
              {...props}
            />
          ),
          hr: ({ node, ...props }) => (
            <hr
              className={`border-gray-300 ${compact ? "my-3" : "my-6"}`}
              {...props}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
